"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { BoxPlot } from "@/components/tools/stats-descriptive/box-plot";
import {
  Explanation,
  Formula,
  ResultTable,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";
import type { QuantileMethod } from "@/lib/stats/descriptive";
import {
  formatNumberList,
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  type QuantileStep,
  type QuartileReport,
  quartileReport,
} from "@/lib/tool-kit/stats-descriptive/quartiles";

/** English strings. A translated content file overrides these through `ui`. */
export const QUARTILE_CALCULATOR_STRINGS = {
  dataLabel: "Your data — numbers separated by commas, spaces, or new lines",
  dataPlaceholder: "23, 12, 29, 17, 58, 20, 15",
  valuesDetected: "one: value detected|other: values detected",
  methodLabel: "Quartile method",
  methodTukey: "Median of halves (TI-84, most textbooks)",
  methodInclusive: "Inclusive (Excel QUARTILE.INC, R type 7)",
  methodExclusive: "Exclusive (Excel QUARTILE.EXC, type 6)",
  tryExample: "Try an example",
  exampleData: "23, 12, 29, 17, 58, 20, 15, 26, 18, 31, 21, 24",
  clear: "Clear",
  enterPrompt: "Enter at least three numbers to see the quartiles.",
  results: "Results",
  minimum: "Minimum",
  q1: "Q1 (first quartile)",
  median: "Median (Q2)",
  q3: "Q3 (third quartile)",
  maximum: "Maximum",
  iqr: "Interquartile range (IQR)",
  boxPlotTitle: "Box plot",
  boxPlotLabel:
    "Box plot: minimum {min}, Q1 {q1}, median {median}, Q3 {q3}, maximum {max}",
  boxPlotNote:
    "Whiskers reach the furthest values inside the fences; dots are outliers.",
  outliersTitle: "Outliers by Tukey's fences",
  fenceLower: "Lower fence = Q1 − 1.5 × IQR = {q1} − 1.5 × {iqr} = {fence}",
  fenceUpper: "Upper fence = Q3 + 1.5 × IQR = {q3} + 1.5 × {iqr} = {fence}",
  noOutliers: "No outliers — every value lies between the fences.",
  outliersFound:
    "one: {count} value lies outside the fences: {list}|other: {count} values lie outside the fences: {list}",
  workingTitle: "How the quartiles were found",
  sortedLine: "Sorted data (n = {n}): {list}",
  listSeparator: ", ",
  moreValues: "… and {count} more",
  halvesOdd:
    "one: n is odd, so the median ({median}) is left out and each half holds {half} value.|other: n is odd, so the median ({median}) is left out and each half holds {half} values.",
  halvesEven:
    "one: n is even, so the data split into two halves of {half} value.|other: n is even, so the data split into two halves of {half} values.",
  lowerHalf: "Lower half: {list} → Q1 = median of the lower half = {value}",
  upperHalf: "Upper half: {list} → Q3 = median of the upper half = {value}",
  positionInclusive: "Position of a quantile p = 1 + (n − 1) × p",
  positionExclusive: "Position of a quantile p = (n + 1) × p",
  stepInterpolated:
    "{label}: position {position} → {lower} + {fraction} × ({upper} − {lower}) = {value}",
  stepExact: "{label}: position {position} → {value}",
  stepClamped:
    "{label}: position {position} falls outside 1–{n}, so the nearest end value is used: {value}",
  q1Short: "Q1",
  medianShort: "Median",
  q3Short: "Q3",
  iqrShort: "IQR",
  compareTitle: "Your data under all three methods",
  compareMethod: "Method",
  compareNote:
    "The methods agree on the median and differ on Q1 and Q3. The gap narrows as n grows — report which method you used.",
  copySummary: "Copy summary",
  copied: "Copied!",
  summaryText:
    "Minimum = {min}, Q1 = {q1}, median = {median}, Q3 = {q3}, maximum = {max}, IQR = {iqr} ({method})",
} as const;

type Strings = typeof QUARTILE_CALCULATOR_STRINGS;

const METHODS: readonly QuantileMethod[] = ["tukey", "inclusive", "exclusive"];

function methodLabel(method: QuantileMethod, strings: Strings): string {
  if (method === "inclusive") {
    return strings.methodInclusive;
  }
  if (method === "exclusive") {
    return strings.methodExclusive;
  }
  return strings.methodTukey;
}

function formatList(
  values: readonly number[],
  fmt: NumberFormatter,
  strings: Strings
): string {
  return formatNumberList(
    fmt,
    values,
    strings.listSeparator,
    strings.moreValues
  );
}

function stepLine(
  step: QuantileStep,
  label: string,
  n: number,
  fmt: NumberFormatter,
  strings: Strings
): string {
  const values = {
    label,
    n: fmt(n),
    position: formatStat(fmt, step.position),
    value: formatStat(fmt, step.value),
    lower: formatStat(fmt, step.lowerValue),
    upper: formatStat(fmt, step.upperValue),
    fraction: formatStat(fmt, step.fraction),
  };
  if (step.clamped) {
    return t(strings.stepClamped, values);
  }
  if (step.fraction === 0) {
    return t(strings.stepExact, values);
  }
  return t(strings.stepInterpolated, values);
}

function MethodWorking({
  report,
  fmt,
  strings,
  locale,
}: {
  report: QuartileReport;
  fmt: NumberFormatter;
  strings: Strings;
  locale: string;
}) {
  const { halves, steps, summary } = report;
  if (halves) {
    const half = fmt(halves.lower.length);
    return (
      <>
        <Explanation>
          {halves.medianExcluded
            ? tPlural(strings.halvesOdd, locale, halves.lower.length, {
                median: formatStat(fmt, summary.median),
                half,
              })
            : tPlural(strings.halvesEven, locale, halves.lower.length, {
                half,
              })}
        </Explanation>
        <Formula>
          {t(strings.lowerHalf, {
            list: formatList(halves.lower, fmt, strings),
            value: formatStat(fmt, summary.q1),
          })}
        </Formula>
        <Formula>
          {t(strings.upperHalf, {
            list: formatList(halves.upper, fmt, strings),
            value: formatStat(fmt, summary.q3),
          })}
        </Formula>
      </>
    );
  }
  const labels = [strings.q1Short, strings.medianShort, strings.q3Short];
  return (
    <>
      <Formula>
        {report.method === "exclusive"
          ? strings.positionExclusive
          : strings.positionInclusive}
      </Formula>
      {(steps ?? []).map((step, index) => (
        <Formula key={step.p}>
          {stepLine(step, labels[index], report.n, fmt, strings)}
        </Formula>
      ))}
    </>
  );
}

function Outliers({
  report,
  fmt,
  strings,
  locale,
}: {
  report: QuartileReport;
  fmt: NumberFormatter;
  strings: Strings;
  locale: string;
}) {
  const { summary } = report;
  const iqr = formatStat(fmt, summary.iqr);
  const count = summary.outliers.length;
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.outliersTitle}</h3>
      <Formula>
        {t(strings.fenceLower, {
          q1: formatStat(fmt, summary.q1),
          iqr,
          fence: formatStat(fmt, summary.lowerFence),
        })}
      </Formula>
      <Formula>
        {t(strings.fenceUpper, {
          q3: formatStat(fmt, summary.q3),
          iqr,
          fence: formatStat(fmt, summary.upperFence),
        })}
      </Formula>
      <Explanation>
        {count === 0
          ? strings.noOutliers
          : tPlural(strings.outliersFound, locale, count, {
              count: fmt(count),
              list: formatList(summary.outliers, fmt, strings),
            })}
      </Explanation>
    </div>
  );
}

export default function QuartileCalculatorWidget() {
  const strings = useToolUi(QUARTILE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState("");
  const [method, setMethod] = useState<QuantileMethod>("tukey");

  const values = parseNumberList(raw);
  const report = values.length >= 3 ? quartileReport(values, method) : null;
  const num = (value: number) => formatStat(fmt, value);

  const compareRows = report
    ? METHODS.map((each) => {
        const summary = (quartileReport(values, each) as QuartileReport)
          .summary;
        return [
          methodLabel(each, strings),
          num(summary.q1),
          num(summary.median),
          num(summary.q3),
          num(summary.iqr),
        ];
      })
    : [];

  return (
    <>
      <section className="space-y-4">
        <TextAreaField
          hint={
            <>
              {fmt(values.length)}
              {tPlural(strings.valuesDetected, locale, values.length)}
            </>
          }
          id="qc-data"
          label={strings.dataLabel}
          onChange={setRaw}
          placeholder={strings.dataPlaceholder}
          value={raw}
        />
        <PillGroup
          label={strings.methodLabel}
          name="qc-method"
          onChange={setMethod}
          options={METHODS.map((value) => ({
            value,
            label: methodLabel(value, strings),
          }))}
          value={method}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setRaw(strings.exampleData)}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={raw === ""}
            onClick={() => setRaw("")}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {report ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite">
            <StatGrid>
              <StatCard
                label={strings.minimum}
                value={num(report.summary.min)}
              />
              <StatCard
                emphasis
                label={strings.q1}
                value={num(report.summary.q1)}
              />
              <StatCard
                label={strings.median}
                value={num(report.summary.median)}
              />
              <StatCard
                emphasis
                label={strings.q3}
                value={num(report.summary.q3)}
              />
              <StatCard
                label={strings.maximum}
                value={num(report.summary.max)}
              />
              <StatCard
                emphasis
                label={strings.iqr}
                value={num(report.summary.iqr)}
              />
            </StatGrid>
          </div>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copySummary}
            text={t(strings.summaryText, {
              min: num(report.summary.min),
              q1: num(report.summary.q1),
              median: num(report.summary.median),
              q3: num(report.summary.q3),
              max: num(report.summary.max),
              iqr: num(report.summary.iqr),
              method: methodLabel(method, strings),
            })}
          />

          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.boxPlotTitle}</h3>
            <BoxPlot
              fmt={fmt}
              label={t(strings.boxPlotLabel, {
                min: num(report.summary.min),
                q1: num(report.summary.q1),
                median: num(report.summary.median),
                q3: num(report.summary.q3),
                max: num(report.summary.max),
              })}
              values={{
                ...report.summary,
                whiskerLow: report.whiskerLow,
                whiskerHigh: report.whiskerHigh,
              }}
            />
            <p className="text-muted-foreground text-xs">
              {strings.boxPlotNote}
            </p>
          </div>

          <Outliers
            fmt={fmt}
            locale={locale}
            report={report}
            strings={strings}
          />

          <WorkingPanel title={strings.workingTitle}>
            <Formula>
              {t(strings.sortedLine, {
                n: fmt(report.n),
                list: formatList(report.data, fmt, strings),
              })}
            </Formula>
            <MethodWorking
              fmt={fmt}
              locale={locale}
              report={report}
              strings={strings}
            />
          </WorkingPanel>

          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.compareTitle}</h3>
            <ResultTable
              head={[
                strings.compareMethod,
                strings.q1Short,
                strings.medianShort,
                strings.q3Short,
                strings.iqrShort,
              ]}
              rows={compareRows}
              wrapFirstColumn
            />
            <p className="text-muted-foreground text-xs">
              {strings.compareNote}
            </p>
          </div>
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </>
  );
}
